import React from 'react';
import { Platform, BackHandler, LogBox } from 'react-native';
import { NavigationContainer } from '@react-navigation/native';
import { createNativeStackNavigator } from '@react-navigation/native-stack';

import Menu from './src/pages/Menu';
import Reservation from './src/pages/Reservation';
import ReservationConfirm from './src/pages/ReservationConfirm';
import ResercationCancel from './src/pages/ReservationCancel';
import Header from './src/components/Header';
import {
    type MainProps,
    type MenuProps,
    type ReservationProps,
    type ReservationConfirmProps,
    type ReservationCancelProps,
    type RootStackParamList,
} from './src/types/ScreenTypes';
import ScreenSizeProvider from './src/context/ScreenSizeContext';
import { LanguageProvider } from './src/context/LanguageContext';
import { createTheme, ThemeProvider } from '@rneui/themed';

import { StripeContainer } from './src/context/StripeContainer';

import { ErrorBoundary } from './src/components/ErrorBoundary';

if (Platform.OS === 'web') {
    LogBox.ignoreLogs([
        'BackHandler is not supported on web',
        'transform style array value is deprecated',
        'shadow* style props are deprecated',
        'React.createFactory() is deprecated',
        'TouchableOpacity is deprecated',
        'TouchableWithoutFeedback is deprecated',
    ]);
    const noop = () => ({ remove: () => {} });
    // Provide no-op listeners so libraries can call BackHandler on web
    BackHandler.addEventListener = noop;
    BackHandler.removeEventListener = () => {};
}

const originalWarn = console.warn;
console.warn = (...args: any[]) => {
    const message = typeof args[0] === 'string' ? args[0] : '';
    const suppressed = [
        'transform style array value is deprecated',
        'shadow style props are deprecated',
        'React.createFactory() is deprecated',
        'TouchableOpacity is deprecated',
        'TouchableWithoutFeedback is deprecated',
        'Image: style.tintColor is deprecated',
        'Animated: `useNativeDriver` is not supported',
    ];
    if (suppressed.some((entry) => message.includes(entry))) {
        return;
    }
    originalWarn(...args);
};

const MenuScreen: React.FC<MenuProps> = ({ navigation, route }) => {
    return <Menu navigation={navigation} route={route} />;
};

const ReservationScreen: React.FC<ReservationProps> = ({
    navigation,
    route,
}) => {
    return <Reservation navigation={navigation} route={route} />;
};

const ReservationConfirmScreen: React.FC<ReservationConfirmProps> = ({
    navigation,
    route,
}) => {
    return <ReservationConfirm navigation={navigation} route={route} />;
};

const ReservationCancelScreen: React.FC<ReservationCancelProps> = ({
    navigation,
    route,
}) => {
    return <ResercationCancel navigation={navigation} route={route} />;
};

const Stack = createNativeStackNavigator<RootStackParamList>();

const theme = createTheme({
    lightColors: {
        primary: '#5D4037',
        text: 'white',
    },
    darkColors: {
        primary: 'black',
        text: '#FFFFFF',
    },
});

export default function App() {
    const config = {
        screens: {
            // Main: '/',
            Reservation: '/',
            ReservationCancel: 'ReservationCancel/:reservation_id?',
        },
    };

    const linking = {
        prefixes: [],
        config,
    };

    return (
        <ErrorBoundary>
            <ScreenSizeProvider>
                <LanguageProvider>
                    <ThemeProvider theme={theme}>
                        <StripeContainer>
                            <NavigationContainer linking={linking}>
                                <Stack.Navigator
                                    screenOptions={{
                                        header: ({
                                            navigation,
                                            route,
                                            options,
                                            back,
                                        }) => {
                                            return (
                                                <Header
                                                    back={back}
                                                    navigation={navigation}
                                                />
                                            );
                                        },
                                    }}
                                >
                                    <Stack.Screen
                                        name="Reservation"
                                        component={ReservationScreen}
                                    />
                                    <Stack.Screen
                                        name="ReservationConfirm"
                                        component={ReservationConfirmScreen}
                                    />
                                    <Stack.Screen
                                        name="ReservationCancel"
                                        component={ReservationCancelScreen}
                                    />
                                </Stack.Navigator>
                            </NavigationContainer>
                        </StripeContainer>
                    </ThemeProvider>
                </LanguageProvider>
            </ScreenSizeProvider>
        </ErrorBoundary>
    );
}
